import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Monitor, Server, Database, MessageCircle, CreditCard, Stamp, Link2, MapPinned, KeyRound, ArrowDown, Send, CheckCircle2, ArrowRight } from "lucide-react";
import { api, BACKEND_URL, errMsg } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { useAuth } from "@/lib/auth";
import { PageHeader, SimBadge, CopyText, Loading } from "@/components/common";

const METHOD = { GET: "bg-sky-50 text-sky-800 border-sky-200", POST: "bg-emerald-50 text-emerald-800 border-emerald-200", PATCH: "bg-amber-50 text-amber-900 border-amber-200", DELETE: "bg-rose-50 text-rose-800 border-rose-200" };
const THIRD = [
  { id: "auth", icon: KeyRound, name: "Auth (Google / Firebase / Cognito)" },
  { id: "whatsapp", icon: MessageCircle, name: "WhatsApp Business · Twilio" },
  { id: "cuenta_nu", icon: CreditCard, name: "Cuenta Nu" },
  { id: "hitchdocs", icon: Stamp, name: "HitchDocs NOM-151" },
  { id: "polygon", icon: Link2, name: "Polygon / Ethereum" },
  { id: "maps", icon: MapPinned, name: "OpenStreetMap · Leaflet" },
];
const WEBHOOKS = [
  { path: "/api/webhooks/whatsapp", provider: "Twilio / MessageBird", events: "MessageStatus (queued → sent → delivered → read), mensajes entrantes con folio", header: "X-HitchGob-Signature (HMAC-SHA256)", secret: "TWILIO_WEBHOOK_SECRET" },
  { path: "/api/webhooks/nu", provider: "Cuenta Nu", events: "charge.settled, charge.failed, debit.executed, debit.failed", header: "X-Nu-Signature (HMAC-SHA256)", secret: "NU_WEBHOOK_SECRET" },
  { path: "/api/webhooks/firebase", provider: "Firebase Auth", events: "user.created, user.deleted, auth.event", header: "X-Firebase-Signature (HMAC-SHA256)", secret: "FIREBASE_WEBHOOK_SECRET" },
];
const FLOW = [
  ["Ciudadano reporta", "Pin en mapa + foto → folio QR-RPT único en < 1 s, hash SHA-256 anclado.", "/app/reportar", "ciudadano"],
  ["Confirmación omnicanal", "WhatsApp con folio; acuses sent/delivered/read vía webhook.", "/app/notificaciones", "ciudadano"],
  ["Intranet valida y turna", "Funcionario valida, asigna dependencia y cuadrilla; el ciudadano recibe aviso y +10 HGT.", "/app/incidencias", "funcionario"],
  ["Coordinación interdependencias", "Tareas bloqueadas hasta completar predecesoras de otras dependencias.", "/app/tareas", "funcionario"],
  ["Trámite + pago Cuenta Nu", "Expediente digital con huellas de documentos → pago instantáneo con tokens → webhook liquida.", "/app/tramites", "ciudadano"],
  ["Emisión con validez legal", "Aprobación: firma ECDSA + sello NOM-151 + tx Polygon. Verificable por QR.", "/app/expedientes", "funcionario"],
  ["Cobranza en campo", "Agente emite boleta con foto y GPS; QR sin papel; mapa de calor guía las cuadrillas.", "/app/campo", "agente"],
  ["Transparencia pública", "Cualquier persona verifica folio o documento y la integridad de la cadena.", "/verificar", null],
];

function Box({ icon: I, title, sub, sim, tone = "bg-white" }) {
  return (
    <div className={`rounded-xl border p-3 ${tone}`}>
      <div className="flex items-center justify-between gap-2"><I className="h-4 w-4 text-primary" />{sim !== undefined && (sim ? <SimBadge /> : <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">ACTIVO</Badge>)}</div>
      <div className="text-sm font-medium mt-2">{title}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function Architecture() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const integ = useFetch("/meta/integrations");
  const eps = useFetch("/meta/endpoints");
  const env = useFetch(user.role !== "ciudadano" ? "/meta/env" : null);
  const [folio, setFolio] = useState("");
  const [whRes, setWhRes] = useState(null);
  const sim = Object.fromEntries((integ.data?.items || []).map((i) => [i.id, i.simulated]));
  const grouped = (eps.data || []).reduce((a, e) => { (a[e.module] = a[e.module] || []).push(e); return a; }, {});

  const testWebhook = async () => {
    try {
      const { data } = await api.post("/webhooks/whatsapp", { From: `whatsapp:${user.phone || "+529980000000"}`, Body: `ESTATUS ${folio}` });
      setWhRes(data); toast.success("Webhook procesado");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Entregables técnicos" title="Arquitectura, API y flujo operativo" subtitle="Mapa vivo de la plataforma: componentes, endpoints REST, webhooks y variables de entorno."
        actions={integ.data && <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-200">Modo integraciones: {integ.data.mode}</Badge>} />
      <Tabs defaultValue="diagram">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="diagram" data-testid="arch-tab-diagram">Diagrama</TabsTrigger>
          <TabsTrigger value="endpoints" data-testid="arch-tab-endpoints">Endpoints</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="arch-tab-webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="env" data-testid="arch-tab-env">Variables .env</TabsTrigger>
          <TabsTrigger value="ux" data-testid="arch-tab-ux">Flujo UX</TabsTrigger>
        </TabsList>

        <TabsContent value="diagram">
          <Card><CardContent className="p-5" data-testid="architecture-diagram">
            <div className="grid gap-3 md:grid-cols-3">
              <Box icon={Monitor} title="Portal ciudadano (React SPA / PWA)" sub="Reportes, trámites, pagos, lealtad" />
              <Box icon={Monitor} title="Intranet gubernamental" sub="Tableros Chart.js, incidencias, tareas" />
              <Box icon={Monitor} title="App de campo (móvil web)" sub="Multas con foto + GPS, boletas QR" />
            </div>
            <div className="flex justify-center my-2 text-muted-foreground"><ArrowDown className="h-5 w-5" /><span className="text-xs ml-1">HTTPS · /api · cookie de sesión httpOnly · polling 3–10 s</span></div>
            <div className="rounded-xl border-2 border-primary/30 bg-[#DFF3F6] p-4">
              <div className="flex items-center gap-2 font-medium"><Server className="h-4 w-4 text-primary" />API Gateway · FastAPI (Python, serverless-ready: AWS Lambda / Cloud Functions)</div>
              <div className="grid gap-2 mt-3 sm:grid-cols-2 lg:grid-cols-4">
                {["Comunidad", "Intranet", "Ventanilla + Pagos", "Validez legal", "Multas y cobranza", "Lealtad HGT", "Webhooks", "Notificaciones"].map((m) => (
                  <div key={m} className="rounded-lg bg-white border px-3 py-2 text-sm">{m}</div>
                ))}
              </div>
              <div className="text-[11px] text-muted-foreground mt-2">Adaptadores intercambiables (simulated ↔ live) por INTEGRATIONS_MODE · procesamiento idempotente de eventos</div>
            </div>
            <div className="grid md:grid-cols-[1fr_2fr] gap-3 mt-3">
              <div>
                <div className="flex justify-center my-2 text-muted-foreground"><ArrowDown className="h-5 w-5" /></div>
                <Box icon={Database} title="MongoDB" sub="reports, expedientes, payments, fines, ledger, notifications, token_ledger, webhook_events" tone="bg-white" />
              </div>
              <div>
                <div className="flex justify-center my-2 text-muted-foreground"><ArrowDown className="h-5 w-5" /><span className="text-xs ml-1">REST + webhooks firmados HMAC</span></div>
                <div className="grid gap-2 grid-cols-2 lg:grid-cols-3">
                  {THIRD.map((t) => <Box key={t.id} icon={t.icon} title={t.name} sim={sim[t.id]} />)}
                </div>
              </div>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="endpoints">
          {!eps.data ? <Loading /> : (
            <Card><CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-3">{eps.data.length} endpoints REST generados desde el router en vivo · base <span className="font-mono">{BACKEND_URL}</span></div>
              <Accordion type="multiple" defaultValue={[Object.keys(grouped)[0]]}>
                {Object.entries(grouped).sort().map(([mod, list]) => (
                  <AccordionItem key={mod} value={mod}>
                    <AccordionTrigger data-testid={`endpoints-module-${mod}`}>{mod} <span className="text-xs text-muted-foreground ml-2">{list.length}</span></AccordionTrigger>
                    <AccordionContent className="space-y-1.5">
                      {list.map((e) => (
                        <div key={e.method + e.path} className="flex flex-wrap items-center gap-2 text-sm">
                          <Badge variant="outline" className={`font-mono w-16 justify-center ${METHOD[e.method]}`}>{e.method}</Badge>
                          <span className="font-mono text-xs">{e.path}</span>
                          {e.summary && <span className="text-xs text-muted-foreground">— {e.summary}</span>}
                        </div>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="webhooks">
          <div className="grid gap-3 lg:grid-cols-3">
            {WEBHOOKS.map((w) => (
              <Card key={w.path}><CardHeader className="pb-2"><CardTitle className="text-base">{w.provider}</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="text-xs text-muted-foreground">URL para configurar en el proveedor</div>
                  <CopyText value={`${BACKEND_URL}${w.path}`} display={w.path} />
                  <div><span className="text-xs text-muted-foreground">Eventos:</span> {w.events}</div>
                  <div><span className="text-xs text-muted-foreground">Firma:</span> <span className="font-mono text-xs">{w.header}</span></div>
                  <div><span className="text-xs text-muted-foreground">Secreto:</span> <span className="font-mono text-xs">{w.secret}</span></div>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card className="mt-4"><CardHeader className="pb-2"><CardTitle className="text-base">Probar webhook entrante de WhatsApp</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">Simula que un ciudadano escribe “ESTATUS &lt;folio&gt;” al número oficial: el sistema responde con el estatus.</p>
              <div className="flex gap-2 max-w-lg">
                <Input value={folio} onChange={(e) => setFolio(e.target.value)} placeholder="QR-RPT-2026-000001" className="font-mono" data-testid="webhook-test-folio-input" />
                <Button onClick={testWebhook} disabled={!folio} data-testid="webhook-test-send-button"><Send className="h-4 w-4 mr-1" />Enviar</Button>
              </div>
              {whRes && <div className="text-sm rounded-md bg-secondary p-3 font-mono" data-testid="webhook-test-result">{JSON.stringify(whRes)} → revisa Notificaciones / Auditoría</div>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="env">
          <Card><CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-3">Placeholders seguros en <span className="font-mono">backend/.env.example</span>. Solo se muestra si cada variable está configurada; nunca su valor.</p>
            {user.role === "ciudadano" ? <div className="text-sm" data-testid="env-restricted">Disponible para funcionarios y agentes (cambia tu rol DEMO).</div> : !env.data ? <Loading /> : (
              <div className="grid gap-3 md:grid-cols-2">
                {env.data.map((g) => (
                  <div key={g.group} className="rounded-lg border p-3">
                    <div className="text-sm font-medium mb-2">{g.group}</div>
                    {g.vars.map((v) => (
                      <div key={v.name} className="flex items-center justify-between text-xs py-0.5">
                        <span className="font-mono">{v.name}</span>
                        {v.configured ? <span className="text-emerald-700">configurada</span> : <span className="text-muted-foreground">placeholder</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="ux">
          <ol className="relative border-l-2 border-border ml-3 space-y-4" data-testid="ux-flow">
            {FLOW.map(([t, d, to, role], i) => (
              <li key={t} className="ml-6">
                <span className="absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white text-xs font-semibold">{i + 1}</span>
                <Card className="card-hover"><CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="max-w-xl"><div className="font-medium flex items-center gap-2">{t}{role && <Badge variant="outline" className="text-[10px]">{role}</Badge>}</div><p className="text-sm text-muted-foreground">{d}</p></div>
                  <Button size="sm" variant="outline" onClick={() => navigate(to)} disabled={!!role && role !== user.role} data-testid={`ux-step-${i + 1}`}>
                    {role && role !== user.role ? `Requiere rol ${role}` : <>Probar <ArrowRight className="h-3.5 w-3.5 ml-1" /></>}
                  </Button>
                </CardContent></Card>
              </li>
            ))}
            <li className="ml-6"><span className="absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600 text-white"><CheckCircle2 className="h-4 w-4" /></span>
              <div className="text-sm text-muted-foreground pt-0.5">Cada paso queda registrado en la bitácora encadenada y es auditable.</div></li>
          </ol>
        </TabsContent>
      </Tabs>
    </div>
  );
}
